import { Instagram, TrendingUp, Mail, Eye, X, MessageCircle } from 'lucide-react';
import type { RisingCreator } from '../../hooks/useRisingCreators';
import RisingScoreCircle from './RisingScoreCircle';
import StatusBadge from './StatusBadge';
import SourceBadge from './SourceBadge';

interface CreatorCardProps {
  creator: RisingCreator;
  onDetails: (creator: RisingCreator) => void;
  onContact: (creator: RisingCreator) => void;
  onReject: (creator: RisingCreator) => void;
}

function formatFollowers(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

export default function CreatorCard({ creator, onDetails, onContact, onReject }: CreatorCardProps) {
  const growth = creator.growth_rate_30d;
  const hasEmail = !!creator.email;

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md hover:border-blue-200 transition-all duration-200 cursor-pointer flex flex-col gap-3"
      onClick={() => onDetails(creator)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img
              src={creator.profile_picture_url || 'https://via.placeholder.com/60'}
              alt={creator.username}
              className="w-14 h-14 rounded-full object-cover border-2 border-gray-100"
              onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/60'; }}
            />
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-pink-100 rounded-full flex items-center justify-center border border-white">
              <Instagram className="w-2.5 h-2.5 text-pink-600" />
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-gray-900 text-sm truncate">
                {creator.full_name || creator.username}
              </span>
              {hasEmail && (
                <Mail className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" title="Email détecté dans la bio" />
              )}
            </div>
            <span className="text-xs text-gray-500">@{creator.username}</span>
          </div>
        </div>
        <RisingScoreCircle score={creator.rising_score} />
      </div>

      <div className="flex items-center gap-3 text-sm">
        <div className="flex flex-col">
          <span className="font-bold text-gray-900">{formatFollowers(creator.followers_count)}</span>
          {growth !== null && growth !== undefined && (
            <span className={`text-xs font-medium flex items-center gap-0.5 ${growth >= 0 ? 'text-green-600' : 'text-red-500'}`}>
              <TrendingUp className="w-3 h-3" />
              {growth >= 0 ? '+' : ''}{growth.toFixed(1)}% 30j
            </span>
          )}
        </div>
        {creator.engagement_rate !== null && (
          <div className="flex flex-col">
            <span className="text-xs text-gray-500">Engagement</span>
            <span className={`text-sm font-semibold ${
              (creator.engagement_rate || 0) >= 4 ? 'text-green-600' :
              (creator.engagement_rate || 0) >= 2 ? 'text-amber-600' : 'text-gray-600'
            }`}>
              {creator.engagement_rate?.toFixed(2)}%
            </span>
          </div>
        )}
        {creator.avg_views_reels !== null && creator.avg_views_reels !== undefined && creator.avg_views_reels > 0 && (
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 flex items-center gap-1"><Eye className="w-3 h-3" /> Reels</span>
            <span className="text-sm font-semibold text-gray-700">{formatFollowers(creator.avg_views_reels)}</span>
          </div>
        )}
      </div>

      {creator.bio && (
        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
          {creator.bio}
        </p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <SourceBadge source={creator.source} />
        <StatusBadge status={creator.status} />
      </div>

      <div
        className="flex items-center gap-2 pt-1 border-t border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => onContact(creator)}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          Approcher
        </button>
        <button
          onClick={() => onDetails(creator)}
          className="flex-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition-colors"
        >
          Voir détails
        </button>
        <button
          onClick={() => onReject(creator)}
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
          title="Rejeter"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
